import ServiceLanding, { type ServiceLandingProps } from "@/components/feature/ServiceLanding";
import { buildBreadcrumbSchema, buildServiceSchema } from "@/seo/schema";
import { SITE_URL } from "@/pages/home/constants";

const PAGE_URL = `${SITE_URL}passeio-de-lancha-capitolio`;

const data: ServiceLandingProps = {
  seo: {
    title: "Passeio de Lancha em Capitólio | Lago de Furnas e Cânions",
    description:
      "Passeio de lancha em Capitólio no Lago de Furnas: roteiro pelos cânions, Lagoa Azul e cachoeiras, em passeio compartilhado ou exclusivo. Reserve no WhatsApp.",
    keywords:
      "passeio de lancha em Capitólio, passeio de lancha Capitólio MG, passeio no Lago de Furnas, passeio de lancha compartilhado, passeio de lancha exclusivo",
    canonical: PAGE_URL,
    jsonLd: [
      buildServiceSchema({
        name: "Passeio de lancha em Capitólio",
        description:
          "Passeio de lancha em Capitólio pelo Lago de Furnas, com roteiro pelos cânions, Lagoa Azul, Vale dos Tucanos e cachoeiras, nas modalidades compartilhada e exclusiva.",
        url: PAGE_URL,
        serviceType: "Passeio de lancha",
      }),
      buildBreadcrumbSchema([
        { name: "Início", url: SITE_URL },
        { name: "Passeio de Lancha em Capitólio", url: PAGE_URL },
      ]),
    ],
  },
  breadcrumbLabel: "Passeio de Lancha em Capitólio",
  tagline: "Viva o Mar de Minas",
  h1: "Passeio de Lancha em Capitólio no Lago de Furnas",
  intro:
    "Navegue pelos cenários mais impressionantes de Capitólio a bordo de lanchas modernas e seguras. Escolha o passeio compartilhado, com outros turistas, ou o passeio exclusivo, privativo para a sua família ou grupo — sempre com tripulação experiente e atendimento personalizado.",
  heroImage:
    "https://static.readdy.ai/image/2b7eadfcd80fca33a6975f7d0c97ecdf/2f056ec6aca4a3cdd194809107842dce.jpeg",
  heroAlt:
    "Lancha branca em alta velocidade nas águas do Lago de Furnas durante passeio de lancha em Capitólio",
  heroImagePosition: "object-[64%_50%] lg:object-center",
  heroMobileBanner: true,
  reviewsSubtitle:
    "Quem já fez o passeio de lancha em Capitólio com a gente conta como foi — notas e comentários reais do Google e do TripAdvisor.",
  furnasSections: true,
  showGallery: false,
  priceNote: "A partir de R$ 100 por pessoa",
  highlights: [
    { icon: "ri-shield-check-line", title: "Cadastur", description: "Embarcações homologadas" },
    { icon: "ri-time-line", title: "3 horas de passeio", description: "Roteiro completo no lago" },
    { icon: "ri-group-line", title: "Compartilhado ou exclusivo", description: "Do jeito que você preferir" },
  ],
  featuresTitle: "O que está incluso no passeio de lancha em Capitólio",
  featuresSubtitle:
    "Cuidamos de cada detalhe para que o seu único trabalho seja aproveitar o Lago de Furnas com tranquilidade.",
  features: [
    {
      icon: "ri-shield-star-line",
      title: "Segurança e tripulação experiente",
      description:
        "Embarcações regulamentadas, coletes salva-vidas e uma equipe que conhece cada canto do Lago de Furnas.",
    },
    {
      icon: "ri-route-line",
      title: "Roteiro pelos melhores atrativos",
      description:
        "Cânions de Capitólio, Lagoa Azul, Vale dos Tucanos, Cachoeira da Gruta, bares flutuantes e a Cachoeira das Orquídeas, com parada para banho.",
    },
    {
      icon: "ri-ship-line",
      title: "Conforto a bordo",
      description:
        "Lanchas modernas com som Bluetooth, cooler e, em alguns modelos, churrasqueira e banheiro a bordo.",
    },
  ],
  stepsTitle: "Como reservar o seu passeio de lancha em Capitólio",
  stepsSubtitle: "Em poucos minutos você garante o seu passeio no coração do Mar de Minas.",
  steps: [
    {
      icon: "ri-whatsapp-line",
      title: "Fale com a gente",
      description: "Chame a Eco Tour Turismo no WhatsApp e conte o que você procura.",
    },
    {
      icon: "ri-calendar-check-line",
      title: "Escolha os detalhes",
      description: "Defina a data, o número de pessoas e se prefere passeio compartilhado ou exclusivo.",
    },
    {
      icon: "ri-anchor-line",
      title: "Embarque e aproveite",
      description: "No dia, embarque no Complexo Furnas Aventura, à beira do Lago de Furnas, e curta o passeio.",
    },
  ],
  galleryTitle: "O que você visita no passeio de lancha",
  gallerySubtitle: "Um roteiro completo pelos lugares mais bonitos de Capitólio e do Lago de Furnas.",
  gallery: [
    {
      image:
        "https://static.readdy.ai/image/2b7eadfcd80fca33a6975f7d0c97ecdf/bd41190ddd61365610701b4e77a1b8e7.jpeg",
      alt: "Cânions de Capitólio vistos durante passeio de lancha no Lago de Furnas",
      caption: "Cânions de Capitólio",
    },
    {
      image:
        "https://static.readdy.ai/image/2b7eadfcd80fca33a6975f7d0c97ecdf/48918e557a883f272772a4c36c5dda87.jpeg",
      alt: "Lagoa Azul de Capitólio com águas cristalinas no Lago de Furnas",
      caption: "Lagoa Azul",
    },
    {
      image:
        "https://static.readdy.ai/image/2b7eadfcd80fca33a6975f7d0c97ecdf/88c6d614b4e2dd1868fb830a4d8e3a2a.jpeg",
      alt: "Vale dos Tucanos em Capitólio durante passeio de lancha no Mar de Minas",
      caption: "Vale dos Tucanos",
    },
    {
      image:
        "https://static.readdy.ai/image/2b7eadfcd80fca33a6975f7d0c97ecdf/71a20f555e9d7ab82d34ed62374259ba.jpeg",
      alt: "Cachoeira da Gruta em Capitólio no roteiro de passeio de lancha",
      caption: "Cachoeira da Gruta",
    },
    {
      image:
        "https://static.readdy.ai/image/2b7eadfcd80fca33a6975f7d0c97ecdf/31d3b346618e486029d4fa265d7c1660.png",
      alt: "Bares flutuantes do Lago de Furnas em Capitólio",
      caption: "Bares Flutuantes",
    },
    {
      image:
        "https://static.readdy.ai/image/2b7eadfcd80fca33a6975f7d0c97ecdf/a013fc67a0b50d28589d624f16e42f74.jpeg",
      alt: "Cachoeira das Orquídeas, parada para banho no Lago de Furnas em Capitólio",
      caption: "Cachoeira das Orquídeas · parada para banho",
    },
  ],
  faqs: [
    {
      question: "Quanto custa um passeio de lancha em Capitólio?",
      answer:
        "O passeio compartilhado começa em R$ 100 por pessoa e o passeio exclusivo (privativo) a partir de R$ 700 por lancha. Os valores variam conforme o roteiro, o número de pessoas e a temporada — confirme o valor atualizado pelo WhatsApp.",
    },
    {
      question: "Quanto tempo dura o passeio de lancha no Lago de Furnas?",
      answer:
        "O passeio compartilhado dura cerca de 3 horas navegando pelo Lago de Furnas. No passeio exclusivo, o tempo e o roteiro são definidos por você, junto com a nossa equipe.",
    },
    {
      question: "Qual a diferença entre passeio compartilhado e exclusivo?",
      answer:
        "No compartilhado você divide a lancha com outros turistas e paga por pessoa. No exclusivo a lancha é privativa para o seu grupo, com roteiro e horário de saída personalizados.",
    },
    {
      question: "O passeio passa pelos Cânions e pela Lagoa Azul?",
      answer:
        "Sim. Os Cânions de Capitólio e a Lagoa Azul estão entre os principais pontos do nosso roteiro, além do Vale dos Tucanos, da Cachoeira da Gruta e dos bares flutuantes.",
    },
    {
      question: "Onde é o ponto de embarque?",
      answer:
        "O embarque acontece no Complexo Furnas Aventura, à beira do Lago de Furnas, em Capitólio (MG), com estacionamento gratuito e vestiário amplo.",
    },
  ],
  ctaTitle: "Pronto para viver o Mar de Minas?",
  ctaText:
    "Fale agora com a Eco Tour Turismo e reserve o seu passeio de lancha em Capitólio — fechando com a gente, você ainda pode ganhar ingressos e descontos em parques e cachoeiras.",
  relatedCtaLabel: "Ver site completo",
  related: [
    {
      to: "/passeio-de-jet-ski-capitolio",
      title: "Passeio de Jet Ski em Capitólio",
      description: "Adrenalina no Lago de Furnas com passeio guiado e locação de moto aquática.",
      icon: "ri-speed-up-line",
    },
  ],
};

export default function PasseioDeLanchaCapitolio() {
  return <ServiceLanding {...data} />;
}